import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { usePhone } from '../telephony/phoneContext';
import { CallStatusBar } from './CallStatusBar';
import { terminalCallStatuses } from './format';

// ConfirmationCall: Anruf zur Terminbestätigung direkt aus der Bestätigung (Thomas 13.10.2026) – über die Firmennummer und
// protokolliert, ohne Akquise-Regeln (nur Sperren und Anrufzeiten) und ohne Akquise-Ergebnis; das Ergebnis kommt danach über
// „Bestätigt“, „Nicht erreicht“ oder „Abgesagt“.
export function ConfirmationCall({ appointmentId, leadId }: { appointmentId: string; leadId: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const phone = usePhone();
  const [callId, setCallId] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: () => {
      if (phone.browserMode) phone.expect();
      return papi.startCall({ lead_id: leadId, appointment_id: appointmentId });
    },
    onSuccess: (c) => setCallId(c.id),
    onError: () => phone.cancelExpect(),
  });
  const call = useQuery({
    queryKey: projectKeys.call(project.id, callId ?? ''),
    queryFn: ({ signal }) => papi.call(callId as string, signal),
    enabled: !!callId,
    refetchInterval: (q) => (q.state.data && terminalCallStatuses.includes(q.state.data.status) ? false : 1500),
  });
  const end = useMutation({
    mutationFn: () => {
      phone.hangup();
      return papi.endCall(callId as string);
    },
  });
  const c = callId ? call.data : undefined;
  const done = !!c && terminalCallStatuses.includes(c.status);
  const notReady = phone.browserMode && phone.state !== 'ready';
  return (
    <div className="confirmation-call">
      {(!c || done) && (
        <button type="button" className="btn btn-small" disabled={start.isPending || notReady} onClick={() => start.mutate()}>
          {done ? 'Erneut anrufen' : 'Anrufen'}
        </button>
      )}
      {c && (
        <CallStatusBar
          status={c.status}
          startedAt={c.answered_at ?? c.started_at}
          endedAt={c.ended_at}
          failure={c.failure_reason}
          onEnd={done ? undefined : () => end.mutate()}
          muted={!done && phone.state === 'in-call' ? phone.muted : undefined}
          onMute={phone.toggleMute}
          browser={phone.browserMode}
        />
      )}
      {done && <p className="muted small">Bitte das Ergebnis eintragen: bestätigt, nicht erreicht oder abgesagt.</p>}
      {notReady && !c && <p className="muted small">Das Telefon im Browser verbindet sich noch …</p>}
      {start.isError && <MutationError error={start.error} />}
    </div>
  );
}
